import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { THEME_ACCENT_TOKENS, type ThemeAccent } from './themeTokens';

export type ResolvedTheme = 'light' | 'dark';
export type ThemeMode = ResolvedTheme | 'system';

export const THEME_MODE_OPTIONS: Array<{ value: ThemeMode; label: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

export const THEME_ACCENT_OPTIONS: Array<{
  value: ThemeAccent;
  label: string;
  primary: string;
  primaryLight: string;
}> = [
  {
    value: 'green',
    label: THEME_ACCENT_TOKENS.green.label,
    primary: THEME_ACCENT_TOKENS.green.light.primary,
    primaryLight: THEME_ACCENT_TOKENS.green.light.primaryLight,
  },
  {
    value: 'blue',
    label: THEME_ACCENT_TOKENS.blue.label,
    primary: THEME_ACCENT_TOKENS.blue.light.primary,
    primaryLight: THEME_ACCENT_TOKENS.blue.light.primaryLight,
  },
  {
    value: 'gold',
    label: THEME_ACCENT_TOKENS.gold.label,
    primary: THEME_ACCENT_TOKENS.gold.light.primary,
    primaryLight: THEME_ACCENT_TOKENS.gold.light.primaryLight,
  },
  {
    value: 'rose',
    label: THEME_ACCENT_TOKENS.rose.label,
    primary: THEME_ACCENT_TOKENS.rose.light.primary,
    primaryLight: THEME_ACCENT_TOKENS.rose.light.primaryLight,
  },
  {
    value: 'lavender',
    label: THEME_ACCENT_TOKENS.lavender.label,
    primary: THEME_ACCENT_TOKENS.lavender.light.primary,
    primaryLight: THEME_ACCENT_TOKENS.lavender.light.primaryLight,
  },
  {
    value: 'coral',
    label: THEME_ACCENT_TOKENS.coral.label,
    primary: THEME_ACCENT_TOKENS.coral.light.primary,
    primaryLight: THEME_ACCENT_TOKENS.coral.light.primaryLight,
  },
];

interface ThemeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  accent: ThemeAccent;
  setAccent: (accent: ThemeAccent) => void;
  resolvedTheme: ResolvedTheme;
  theme: ResolvedTheme;
  toggleTheme: () => void;
}

const THEME_MODE_STORAGE_KEY = 'tark-theme';
const THEME_ACCENT_STORAGE_KEY = 'tark-accent';
const DEFAULT_MODE: ThemeMode = 'light';
const DEFAULT_ACCENT: ThemeAccent = 'green';

const THEME_MODES = new Set<ThemeMode>(THEME_MODE_OPTIONS.map((option) => option.value));
const THEME_ACCENTS = new Set<ThemeAccent>(THEME_ACCENT_OPTIONS.map((option) => option.value));

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

export const useThemeProvider = () => {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [accent, setAccentState] = useState<ThemeAccent>(readStoredAccent);
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);
  const resolvedTheme = mode === 'system' ? systemTheme : mode;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
    root.dataset.accent = accent;
    safelySetStorage(THEME_MODE_STORAGE_KEY, mode);
    safelySetStorage(THEME_ACCENT_STORAGE_KEY, accent);
  }, [accent, mode, resolvedTheme]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setSystemTheme(mediaQuery.matches ? 'dark' : 'light');
    handleChange();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const setMode = useCallback((nextMode: ThemeMode) => {
    setModeState(isThemeMode(nextMode) ? nextMode : DEFAULT_MODE);
  }, []);

  const setAccent = useCallback((nextAccent: ThemeAccent) => {
    setAccentState(isThemeAccent(nextAccent) ? nextAccent : DEFAULT_ACCENT);
  }, []);

  const toggleTheme = useCallback(() => {
    setModeState((currentMode) => {
      const currentResolved = currentMode === 'system' ? getSystemTheme() : currentMode;
      return currentResolved === 'dark' ? 'light' : 'dark';
    });
  }, []);

  return useMemo(
    () => ({
      mode,
      setMode,
      accent,
      setAccent,
      resolvedTheme,
      theme: resolvedTheme,
      toggleTheme,
    }),
    [accent, mode, resolvedTheme, setAccent, setMode, toggleTheme]
  );
};

function readStoredMode(): ThemeMode {
  if (typeof window === 'undefined') {
    return DEFAULT_MODE;
  }

  const stored = safelyGetStorage(THEME_MODE_STORAGE_KEY);
  return isThemeMode(stored) ? stored : DEFAULT_MODE;
}

function readStoredAccent(): ThemeAccent {
  if (typeof window === 'undefined') {
    return DEFAULT_ACCENT;
  }

  const stored = safelyGetStorage(THEME_ACCENT_STORAGE_KEY);
  return isThemeAccent(stored) ? stored : DEFAULT_ACCENT;
}

function isThemeMode(value: unknown): value is ThemeMode {
  return typeof value === 'string' && THEME_MODES.has(value as ThemeMode);
}

function isThemeAccent(value: unknown): value is ThemeAccent {
  return typeof value === 'string' && THEME_ACCENTS.has(value as ThemeAccent);
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function safelyGetStorage(key: string) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safelySetStorage(key: string, value: string) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures in privacy-restricted browsers.
  }
}
